import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface UseImageUploadFieldOptions {
  value: string;
  onChange: (url: string) => void;
  onUpload: (file: File) => Promise<string>;
  disabled?: boolean;
}

export const useImageUploadField = ({ value, onChange, onUpload, disabled }: UseImageUploadFieldOptions) => {
  const { t } = useTranslation("ui");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setIsUploading(true);
      setError(null);
      try {
        const url = await onUpload(file);
        onChange(url);
      } catch (uploadError) {
        setError(uploadError instanceof Error ? uploadError.message : t("Failed to upload image"));
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, onChange, t]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      // Reset so selecting the same file again still fires onChange
      e.target.value = "";
    },
    [handleFile]
  );

  const openFilePicker = useCallback(() => {
    if (disabled || isUploading) return;
    fileInputRef.current?.click();
  }, [disabled, isUploading]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || isUploading) return;
      setIsDragging(true);
    },
    [disabled, isUploading]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled || isUploading) return;
      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFile(file);
      }
    },
    [disabled, isUploading, handleFile]
  );

  const handleRemove = useCallback(() => {
    if (disabled) return;
    setError(null);
    onChange("");
  }, [disabled, onChange]);

  return {
    t,
    fileInputRef,
    isDragging,
    isUploading,
    error,
    hasImage: !!value,
    handleInputChange,
    openFilePicker,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemove,
  };
};
